import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { clientAPI } from '../lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useDropzone } from 'react-dropzone';
import { FileText, Upload, User, ClipboardList, Receipt, CalendarDays, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';

import { ClientPortalHeader, WelcomeBanner, OrganizationFooter } from '../components/client-portal/ClientPortalLayout';
import { ClientProfileTab } from '../components/client-portal/ClientProfileTab';
import { ClientCaseStatusTab } from '../components/client-portal/ClientCaseStatusTab';
import { ClientDocumentsTab } from '../components/client-portal/ClientDocumentsTab';
import { ClientFormsTab } from '../components/client-portal/ClientFormsTab';
import { ClientInvoicesTab } from '../components/client-portal/ClientInvoicesTab';
import { ClientMeetingsTab } from '../components/client-portal/ClientMeetingsTab';

// ── Helpers ──
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return dateStr; }
};

const getStatusColor = (status) => {
  const colors = { Active: 'bg-success/10 text-success', Pending: 'bg-warning/10 text-warning', Approved: 'bg-primary/10 text-primary', Denied: 'bg-error/10 text-error' };
  return colors[status] || 'bg-muted text-foreground';
};

export default function ClientPortalDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState('');
  const [clientData, setClientData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [profileCompletion, setProfileCompletion] = useState(null);
  const [formData, setFormData] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [invoiceTotals, setInvoiceTotals] = useState({ total_due: 0, total_paid: 0 });
  const [processingPayment, setProcessingPayment] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [meetings, setMeetings] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);

  // ── Data fetching ──
  const fetchData = async () => {
    try {
      const [caseRes, docsRes, formsRes, completionRes, invoicesRes, meetingsRes, slotsRes] = await Promise.all([
        clientAPI.getCase(),
        clientAPI.getDocuments(),
        clientAPI.getForms(),
        clientAPI.getProfileCompletion(),
        clientAPI.getInvoices().catch(() => ({ data: { invoices: [], total_due: 0, total_paid: 0 } })),
        clientAPI.getMeetings().catch(() => ({ data: [] })),
        clientAPI.getAvailableSlots().catch(() => ({ data: [] }))
      ]);
      setCaseData(caseRes.data);
      setDocuments(docsRes.data.documents || []);
      setForms(formsRes.data.forms || []);
      setProfileCompletion(completionRes.data);
      setInvoices(invoicesRes.data.invoices || []);
      setInvoiceTotals({ total_due: invoicesRes.data.total_due || 0, total_paid: invoicesRes.data.total_paid || 0 });
      setMeetings(meetingsRes.data || []);
      setAvailableSlots(slotsRes.data || []);

      const c = caseRes.data.case || {};
      setFormData({
        name: c.name || '', dob: c.dob || '', country: c.country || '',
        telephone: c.telephone || '', address: c.address || '', email: c.email || '',
        emergency_contact_name: c.emergency_contact_name || '', emergency_contact_phone: c.emergency_contact_phone || '',
        preferred_language: c.preferred_language || '', marital_status: c.marital_status || '',
        employer_name: c.employer_name || '', employer_address: c.employer_address || '', occupation: c.occupation || ''
      });
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('client_token');
        localStorage.removeItem('client_data');
        navigate('/client-portal');
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('client_token');
    const storedClient = localStorage.getItem('client_data');
    if (!token) { navigate('/client-portal'); return; }
    if (storedClient) setClientData(JSON.parse(storedClient));

    const sessionId = searchParams.get('session_id');
    const tabParam = searchParams.get('tab');
    if (sessionId) {
      setActiveTab('invoices');
      pollPaymentStatus(sessionId);
    } else if (tabParam) {
      setActiveTab(tabParam);
    }
    fetchData();
  }, [navigate, searchParams]);

  // ── Handlers ──
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) { toast.info('Payment status check timed out. Please check your invoices.'); return; }
    try {
      const res = await clientAPI.getPaymentStatus(sessionId);
      if (res.data.payment_status === 'paid') {
        toast.success('Payment successful! Thank you.');
        fetchData();
        window.history.replaceState({}, '', '/client-portal/dashboard');
        return;
      }
      if (res.data.status === 'expired') { toast.error('Payment session expired. Please try again.'); return; }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch {}
  };

  const handlePayInvoice = async (invoiceId) => {
    setProcessingPayment(true);
    try {
      const res = await clientAPI.createCheckout(invoiceId, window.location.origin);
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setProcessingPayment(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await clientAPI.updateProfile(formData);
      toast.success('Profile updated successfully!');
      setEditMode(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally { setSaving(false); }
  };

  const handleGenerateForm = async () => {
    if (!selectedForm) { toast.error('Please select a form'); return; }
    setGenerating(true);
    try {
      const res = await clientAPI.fillForm(selectedForm);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedForm}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Form downloaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate form');
    } finally { setGenerating(false); }
  };

  const handleBookSlot = async (slotId, slotIndex) => {
    try { await clientAPI.bookSlot(slotId, slotIndex); toast.success('Meeting booked successfully!'); fetchData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to book slot'); }
  };

  const handleRequestReschedule = async () => {
    if (!selectedMeetingId) return;
    try {
      await clientAPI.requestReschedule(selectedMeetingId, rescheduleReason);
      toast.success('Reschedule request submitted');
      setRescheduleDialogOpen(false);
      setRescheduleReason('');
      setSelectedMeetingId(null);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to request reschedule'); }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    setUploading(true);
    try {
      for (const file of acceptedFiles) await clientAPI.uploadDocument(file);
      toast.success(`${acceptedFiles.length} document(s) uploaded successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally { setUploading(false); }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'], 'image/*': ['.png', '.jpg', '.jpeg'], 'application/msword': ['.doc'], 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'] },
    maxSize: 10 * 1024 * 1024
  });

  const handleLogout = () => {
    localStorage.removeItem('client_token');
    localStorage.removeItem('client_data');
    navigate('/client-portal');
    toast.success('Logged out successfully');
  };

  const { isDark, toggleTheme } = useTheme();

  // ── Loading state ──
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const caseInfo = caseData?.case || {};
  const org = caseData?.organization || {};
  const statusHistory = caseData?.status_history || {};

  return (
    <div className="min-h-screen bg-background">
      <ClientPortalHeader clientData={clientData} onLogout={handleLogout} isDark={isDark} onToggleTheme={toggleTheme} />

      <main className="max-w-6xl mx-auto px-6 py-8">
        <WelcomeBanner caseInfo={caseInfo} profileCompletion={profileCompletion} />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-6 lg:w-auto lg:inline-flex">
            <TabsTrigger value="profile" data-testid="tab-profile"><User className="w-4 h-4 mr-2" />My Profile</TabsTrigger>
            <TabsTrigger value="status" data-testid="tab-status"><FileText className="w-4 h-4 mr-2" />Case Status</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents"><Upload className="w-4 h-4 mr-2" />Documents</TabsTrigger>
            <TabsTrigger value="forms" data-testid="tab-forms"><ClipboardList className="w-4 h-4 mr-2" />Forms</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices" className="relative">
              <Receipt className="w-4 h-4 mr-2" />Invoices
              {invoiceTotals.total_due > 0 && <span className="absolute -top-1 -right-1 w-2 h-2 bg-error rounded-full" />}
            </TabsTrigger>
            <TabsTrigger value="meetings" data-testid="tab-meetings" className="relative">
              <CalendarDays className="w-4 h-4 mr-2" />Meetings
              {(meetings.filter(m => m.status === 'scheduled').length > 0 || availableSlots.length > 0) && (
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-primary rounded-full" />
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="space-y-6">
            <ClientProfileTab
              caseInfo={caseInfo} editMode={editMode} setEditMode={setEditMode}
              formData={formData} setFormData={setFormData}
              saving={saving} onSave={handleSaveProfile} formatDate={formatDate}
            />
          </TabsContent>

          <TabsContent value="status" className="space-y-6">
            <ClientCaseStatusTab
              caseInfo={caseInfo} statusHistory={statusHistory}
              formatDate={formatDate} getStatusColor={getStatusColor}
            />
          </TabsContent>

          <TabsContent value="documents" className="space-y-6">
            <ClientDocumentsTab
              documents={documents} uploading={uploading}
              getRootProps={getRootProps} getInputProps={getInputProps}
              isDragActive={isDragActive} formatDate={formatDate}
            />
          </TabsContent>

          <TabsContent value="forms" className="space-y-6">
            <ClientFormsTab
              forms={forms} selectedForm={selectedForm} setSelectedForm={setSelectedForm}
              generating={generating} onGenerate={handleGenerateForm}
            />
          </TabsContent>

          <TabsContent value="invoices" className="space-y-6">
            <ClientInvoicesTab
              invoices={invoices} invoiceTotals={invoiceTotals}
              processingPayment={processingPayment} onPay={handlePayInvoice}
            />
          </TabsContent>

          <TabsContent value="meetings" className="space-y-6">
            <ClientMeetingsTab
              meetings={meetings} availableSlots={availableSlots} onBookSlot={handleBookSlot}
              rescheduleDialogOpen={rescheduleDialogOpen} setRescheduleDialogOpen={setRescheduleDialogOpen}
              rescheduleReason={rescheduleReason} setRescheduleReason={setRescheduleReason}
              onRequestReschedule={handleRequestReschedule}
              onOpenReschedule={(id) => { setSelectedMeetingId(id); setRescheduleDialogOpen(true); }}
            />
          </TabsContent>
        </Tabs>

        <OrganizationFooter org={org} />
      </main>
    </div>
  );
}
